import React from "react";
import { createPortal } from "react-dom";
import "./NumberPad.css";
import { t } from "../i18n";

interface NumberPadProps {
  open: boolean;
  /** Current digit string being edited, e.g. "25". */
  value: string;
  /** Called with the new digit string on any key / backspace. */
  onChange: (next: string) => void;
  /** Primary action — commit and close. */
  onDone: () => void;
  maxLength?: number;
  doneLabel?: string;
  /**
   * Show a "." key (bottom-left) for cents / half-dollar amounts. Onboarding
   * leaves this off (whole dollars); in-app price editing turns it on so a DJ
   * can still set e.g. $12.50.
   */
  allowDecimal?: boolean;
  /**
   * Dim the screen behind the pad; tapping it commits (same as Done). Used on
   * the busy in-app screens so taps don't fall through to the page behind.
   */
  withBackdrop?: boolean;
  /**
   * A quiet line above the keys — the talent price pads put their
   * "Minimum $2 (i)" note here (components/PriceFloorNote).
   */
  note?: React.ReactNode;
}

const DIGITS = ["1", "2", "3", "4", "5", "6", "7", "8", "9"];

/**
 * Shared Request number pad. A light, iOS-familiar keypad rendered as a
 * bottom sheet (portaled to <body>, fixed at the bottom — no dependency on
 * the on-screen-keyboard / visualViewport). One consistent numeric-entry
 * surface for web, Capacitor iOS/Android, and Electron.
 */
export default function NumberPad({
  open,
  value,
  onChange,
  onDone,
  maxLength = 3,
  doneLabel = t("numberPad.done"),
  allowDecimal = false,
  withBackdrop = false,
  note,
}: NumberPadProps) {
  if (!open) return null;

  const pressDigit = (d: string) => {
    const next = value === "0" ? d : value + d;
    if (next.length > maxLength) return;
    onChange(next);
  };

  const pressDot = () => {
    if (value.includes(".")) return; // only one decimal point
    const next = value === "" ? "0." : value + ".";
    if (next.length > maxLength) return;
    onChange(next);
  };

  return createPortal(
    <>
      {withBackdrop && (
        <div
          className="numberpad-backdrop"
          onClick={onDone}
          aria-hidden="true"
        />
      )}
      <div className="numberpad" role="group" aria-label={t("numberPad.numberPad")}>
      {note}
      <div className="numberpad-grid">
        {DIGITS.map((d) => (
          <button
            key={d}
            type="button"
            className="numberpad-key"
            onClick={() => pressDigit(d)}
          >
            {d}
          </button>
        ))}
        {allowDecimal ? (
          <button
            type="button"
            className="numberpad-key"
            onClick={pressDot}
            aria-label={t("numberPad.decimalPoint")}
          >
            .
          </button>
        ) : (
          <span
            className="numberpad-key numberpad-key--ghost"
            aria-hidden="true"
          />
        )}
        <button
          type="button"
          className="numberpad-key"
          onClick={() => pressDigit("0")}
        >
          0
        </button>
        <button
          type="button"
          className="numberpad-key numberpad-key--ghost"
          onClick={() => onChange(value.slice(0, -1))}
          aria-label={t("numberPad.delete")}
        >
          <svg
            viewBox="0 0 24 24"
            width="27"
            height="27"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M21 5H8.6a2 2 0 0 0-1.55.74L2.6 11a1.5 1.5 0 0 0 0 1.9l4.45 5.36A2 2 0 0 0 8.6 19H21a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1z" />
            <path d="m18 9.5-5 5M13 9.5l5 5" />
          </svg>
        </button>
      </div>
        <button type="button" className="numberpad-done" onClick={onDone}>
          {doneLabel}
        </button>
      </div>
    </>,
    document.body
  );
}
